<template>
  <div v-if="show" class="confirm-action-modal" role="presentation">
    <div class="confirm-action-modal__backdrop" @click="emitCancel"></div>

    <section
      class="confirm-action-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-action-modal-title"
      @click.stop
    >
      <header :class="headerClass">
        <div>
          <h3 id="confirm-action-modal-title">{{ title }}</h3>
          <p>{{ subtitle }}</p>
        </div>
        <button
          :class="closeButtonClass"
          type="button"
          aria-label="Cerrar modal"
          :disabled="loading"
          @click="emitCancel"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="confirm-action-modal__body">
        <p class="confirm-action-modal__message">{{ message }}</p>
      </div>

      <footer class="confirm-action-modal__footer">
        <button type="button" class="btn btn-light" :disabled="loading" @click="emitCancel">
          {{ cancelText }}
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="emitConfirm">
          <i v-if="loading" class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>
          <span>{{ confirmText }}</span>
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ConfirmActionModal',
  emits: ['cancel', 'confirm'],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Confirmar acción',
    },
    subtitle: {
      type: String,
      default: 'Confirma para continuar con esta operación.',
    },
    message: {
      type: String,
      default: '¿Deseas continuar con esta acción?',
    },
    confirmText: {
      type: String,
      default: 'Confirmar',
    },
    cancelText: {
      type: String,
      default: 'Cancelar',
    },
    tone: {
      type: String,
      default: 'warning',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    normalizedTone() {
      const allowed = ['warning', 'info', 'danger'];
      const key = String(this.tone || '').trim().toLowerCase();
      return allowed.includes(key) ? key : 'warning';
    },
    headerClass() {
      return [
        'confirm-action-modal__header',
        `confirm-action-modal__header--${this.normalizedTone}`,
      ];
    },
    closeButtonClass() {
      return [
        'confirm-action-modal__close',
        `confirm-action-modal__close--${this.normalizedTone}`,
      ];
    },
  },
  methods: {
    emitCancel() {
      if (this.loading) {
        return;
      }

      this.$emit('cancel');
    },
    emitConfirm() {
      if (this.loading) {
        return;
      }

      this.$emit('confirm');
    },
  },
};
</script>

<style scoped>
.confirm-action-modal {
  position: fixed;
  inset: 0;
  z-index: 2200;
}

.confirm-action-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
}

.confirm-action-modal__dialog {
  position: relative;
  width: min(640px, calc(100vw - 32px));
  margin: 10vh auto 0;
  background: #ffffff;
  border: 1px solid rgba(153, 152, 153, 0.25);
  border-radius: 10.5px;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.24);
  overflow: hidden;
}

.confirm-action-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(153, 152, 153, 0.25);
}

.confirm-action-modal__header--warning {
  background: linear-gradient(90deg, rgba(217, 119, 6, 0.09) 0%, rgba(217, 119, 6, 0.03) 100%);
}

.confirm-action-modal__header--info {
  background: linear-gradient(90deg, rgba(2, 132, 199, 0.1) 0%, rgba(2, 132, 199, 0.03) 100%);
}

.confirm-action-modal__header--danger {
  background: linear-gradient(90deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.03) 100%);
}

.confirm-action-modal__header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.confirm-action-modal__header p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.confirm-action-modal__close {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(153, 152, 153, 0.3);
  background: #ffffff;
  color: #6b7280;
  transition: all 0.2s ease;
}

.confirm-action-modal__close:hover {
  color: #111827;
  transform: translateY(-1px);
}

.confirm-action-modal__close--warning:hover {
  border-color: rgba(217, 119, 6, 0.45);
  background: rgba(217, 119, 6, 0.06);
}

.confirm-action-modal__close--info:hover {
  border-color: rgba(2, 132, 199, 0.45);
  background: rgba(2, 132, 199, 0.08);
}

.confirm-action-modal__close--danger:hover {
  border-color: rgba(220, 38, 38, 0.45);
  background: rgba(220, 38, 38, 0.08);
}

.confirm-action-modal__body {
  padding: 18px 20px;
}

.confirm-action-modal__message {
  margin: 0;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  white-space: pre-line;
}

.confirm-action-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid rgba(153, 152, 153, 0.2);
}

.confirm-action-modal__footer .btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
</style>
